import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, ArrowLeft, CheckCircle, Receipt, Coins, Info } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UploadReceipt() {
  const { referralId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();
  const restaurant = location.state?.restaurant;
  const referral = location.state?.referral;

  const [amount, setAmount] = useState('');
  const [coinsUsed, setCoinsUsed] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/wallet`, { withCredentials: true });
        setWalletBalance(res.data.balance || 0);
      } catch (err) {
        console.error('Failed to fetch wallet balance', err);
      }
    })();
  }, []);

  const maxDiscount = amount ? (parseFloat(amount) * 0.05).toFixed(2) : '0.00';
  const effectiveMaxCoins = Math.min(parseFloat(maxDiscount) || 0, walletBalance);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { toast.error('Amount required'); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('restaurant_id', referral?.restaurant_id || restaurant?.restaurant_id || '');
      formData.append('referral_id', referralId);
      formData.append('amount', amount);
      formData.append('coins_used', coinsUsed || '0');
      if (receiptFile) formData.append('receipt_image', receiptFile);
      await axios.post(`${API}/receipts`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(true);
      toast.success(t('ru_success'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  if (success) return (
    <div className="min-h-screen bg-background pb-20 md:pb-8"><Navbar />
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-8 pt-20 sm:pt-24">
        <Card className="border border-border rounded-2xl text-center"><CardContent className="p-6 sm:p-8">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-8 h-8 text-emerald-600" /></div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('ru_success')}</h2>
          <p className="text-muted-foreground text-sm mb-6 font-sans">{t('ru_success_d')}</p>
          <Button data-testid="go-dashboard-btn" onClick={() => navigate('/dashboard')} className="rounded-full btn-lift">{t('go_dashboard')}</Button>
        </CardContent></Card>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="upload-receipt-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pt-20 sm:pt-24">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground mb-4 sm:mb-6 hover:text-foreground transition-colors font-sans" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> {t('back')}
        </button>
        <div className="mb-6 sm:mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2 font-sans">{t('ru_title')}</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('ru_title')}</h1>
        </div>
        <Card className="border border-border rounded-2xl"><CardContent className="p-5 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            <div className="bg-muted/50 rounded-xl p-3 sm:p-4 flex items-center gap-3">
              <Receipt className="w-5 h-5 text-primary shrink-0" strokeWidth={1.5} />
              <div><p className="text-sm font-semibold font-sans">Referral</p><p className="text-xs text-muted-foreground font-mono">{referral?.referral_code || referralId}</p></div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold font-sans">{t('ru_amount')}</Label>
              <Input data-testid="receipt-amount-input" type="number" step="0.01" min="0" placeholder="es. 45.50" value={amount} onChange={e => setAmount(e.target.value)} className="rounded-lg h-11 sm:h-12 text-base sm:text-lg font-sans" />
            </div>

            {/* Coin discount */}
            {walletBalance > 0 && (
              <div className="space-y-2 border border-primary/20 rounded-xl p-4 bg-primary/5">
                <div className="flex items-center gap-2 mb-2">
                  <Coins className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  <Label className="text-sm font-semibold font-sans">{t('ru_coins_label')}</Label>
                </div>
                <p className="text-xs text-muted-foreground font-sans mb-2">
                  {t('ru_coins_help')} (Saldo: {walletBalance.toFixed(2)} coin, Max: EUR {maxDiscount})
                </p>
                <Input data-testid="coins-used-input" type="number" step="0.01" min="0" max={effectiveMaxCoins}
                  placeholder={`Max ${effectiveMaxCoins.toFixed(2)}`} value={coinsUsed}
                  onChange={e => setCoinsUsed(e.target.value)} className="rounded-lg h-10 font-sans" />
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-semibold font-sans">{t('ru_receipt_img')}</Label>
              <div className="dropzone rounded-xl p-6 sm:p-8 text-center cursor-pointer" onClick={() => document.getElementById('receipt-upload').click()} data-testid="receipt-upload-dropzone">
                {receiptFile ? (
                  <div><CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" /><p className="text-sm font-sans font-medium">{receiptFile.name}</p><p className="text-xs text-muted-foreground font-sans mt-1">{t('click_change')}</p></div>
                ) : (
                  <div><Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" strokeWidth={1.5} /><p className="text-sm font-sans text-muted-foreground">{t('upload')}</p></div>
                )}
                <input id="receipt-upload" type="file" accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif" className="hidden" onChange={e => setReceiptFile(e.target.files[0])} />
              </div>
            </div>
            <Button data-testid="submit-receipt-btn" type="submit" disabled={submitting || !amount} className="w-full rounded-xl h-11 sm:h-12 btn-lift">
              {submitting ? '...' : t('ru_submit')}
            </Button>
          </form>
        </CardContent></Card>
      </div>
    </div>
  );
}
